
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../SEO';
// import { articleContents } from './blogContent';
import { ArrowLeft, Calendar, User, Clock, ArrowRight, Share2, Tag, Search, Mail, Filter, Rss } from 'lucide-react';
import { BLOG_ICON_CSS, replaceEmojisWithIcons } from './BlogIcons';
import { getBlogLanguageTag } from './blogLocales';
import { getArticlePath, getArticleUrl } from './blogPaths';
import { trackAffiliateLinkClick } from '../../services/affiliateAnalytics';

interface BlogProps {
    onBack?: () => void;
}

const SITE_URL = 'https://traffic-creator.com';
const GENERATED_THUMBNAIL_PREFIX = '/blog/assets/img/generated/bright-editorial';

const softenBlogClaimText = (value: string): string => value
    .replace(/No provider can guarantee 100% safety/gi, 'No provider can guarantee zero risk')
    .replace(/100% Safe & Trackable/gi, 'trackable with documented risk controls')
    .replace(/100% Analytics Safe/gi, 'analytics-visible with risk controls')
    .replace(/100% safe human traffic/gi, 'configurable browsing behavior for analytics testing')
    .replace(/100% real human sessions, undetectable by any bot filter/gi, 'real human sessions with lower bot-filter risk')
    .replace(/100% safe/gi, 'risk-controlled')
    .replace(/100% safety/gi, 'risk controls')
    .replace(/undetectable by trending algorithms/gi, 'harder for analytics systems to classify consistently')
    .replace(/undetectable website traffic generator/gi, 'analytics-visible website traffic generator')
    .replace(/\bundetectable\b/gi, 'less conspicuous')
    .replace(/\buntraceable\b/gi, 'privacy-aware')
    .replace(/CTR Manipulation/gi, 'CTR analysis')
    .replace(/Click-Through Rate \(CTR\) manipulation/gi, 'Click-Through Rate (CTR) analysis')
    .replace(/CTR is the 4th most important SEO factor[^<.]*/gi, 'CTR can be one engagement diagnostic among many')
    .replace(/CTR Manipulation is a crucial SEO factor, ranking as the 4th most important/gi, 'CTR analysis can help diagnose search-snippet performance')
    .replace(/ClickSEO's organic search traffic is a significant ranking factor and belongs to those SEO companies that offers paid traffic that offers ctr manipulation that works/gi, 'ClickSEO positions its organic-search traffic around engagement testing, but rankings depend on content quality, technical SEO, links, and search intent')
    .replace(/boost your SEO rankings/gi, 'support your SEO analysis')
    .replace(/boost your SERP rankings/gi, 'support SERP performance analysis')
    .replace(/boost your rankings/gi, 'support search visibility analysis')
    .replace(/skyrocketing your SEO rankings/gi, 'supporting SEO performance analysis')
    .replace(/boosting your website's CTR/gi, "analyzing your website's CTR")
    .replace(/boosting your SEO rankings/gi, 'supporting your SEO analysis')
    .replace(/boost your crypto project rankings/gi, 'support crypto project visibility analysis')
    .replace(/boost your cryptocurrency project rankings/gi, 'support cryptocurrency project visibility analysis')
    .replace(/boost your website's traffic, SEO rankings, and social media presence/gi, 'grow website traffic, analyze SEO visibility, and improve social media presence')
    .replace(/rapidly boost their Google rankings/gi, 'analyze engagement signals in Google search performance')
    .replace(/ascending Google rankings/gi, 'analyzing Google visibility')
    .replace(/absolute GA4 security/gi, 'documented GA4 risk controls')
    .replace(/actually <strong>boost<\/strong> your rankings/gi, '<strong>correlate</strong> with visibility gains')
    .replace(/boost Similarweb ranking/gi, 'support Similarweb visibility analysis')
    .replace(/boost its ranking/gi, 'support visibility analysis')
    .replace(/boost your ranking/gi, 'support visibility analysis')
    .replace(/boost the ranking/gi, 'support visibility analysis')
    .replace(/rank your website on the most competitive queries/gi, 'test performance around competitive queries')
    .replace(/rank on the first page of Google/gi, 'compete in Google search results')
    .replace(/Page 1 rankings/gi, 'search visibility gains')
    .replace(/outrank competitors/gi, 'compare performance against competitors')
    .replace(/improve your Google ranking/gi, 'correlate with search performance')
    .replace(/improving the ranking of the webpage in the search results/gi, 'influencing perceived engagement around search results')
    .replace(/improve your site's visibility and ranking/gi, "support your site's visibility analysis")
    .replace(/improve site's visibility and ranking/gi, "support site visibility analysis")
    .replace(/forcing Google to promote your organic ranking/gi, 'testing how engagement signals correlate with organic visibility')
    .replace(/guaranteed undetectable clicks/gi, 'risk-controlled search-session testing')
    .replace(/guaranteed, unquestionable success/gi, 'a more controlled testing setup')
    .replace(/guaranteed organic traffic/gi, 'claimed organic traffic')
    .replace(/enhance SEO rankings, and boost their social media presence/gi, 'analyze SEO visibility, and improve their social media presence')
    .replace(/conversions are not guaranteed/gi, 'conversions are not assured')
    .replace(/network fingerprint matches organic traffic exactly/gi, 'network fingerprint is designed to resemble organic traffic patterns')
    .replace(/generally considered safe for platforms like Google AdSense/gi, 'intended to reduce analytics and ad-platform risk');

const sanitizeMaybeString = (value: unknown): unknown => (
    typeof value === 'string' ? softenBlogClaimText(value) : value
);

const sanitizeBlogArticle = <T extends Record<string, any>>(article: T): T => ({
    ...article,
    title: sanitizeMaybeString(article.title),
    excerpt: sanitizeMaybeString(article.excerpt),
    content: sanitizeMaybeString(article.content),
    seoDescription: sanitizeMaybeString(article.seoDescription),
    category: sanitizeMaybeString(article.category),
    tags: Array.isArray(article.tags)
        ? article.tags.map((tag: unknown) => sanitizeMaybeString(tag))
        : article.tags,
});

const toAbsoluteImageUrl = (image?: string): string => {
    if (!image) return `${SITE_URL}/og-image.png`;
    return image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;
};

const getSafeIsoDate = (date?: string): string => {
    const parsed = date ? new Date(date) : null;
    return parsed && parsed.toString() !== 'Invalid Date'
        ? parsed.toISOString()
        : new Date().toISOString();
};

const stripArticleText = (value?: string): string => {
    if (!value) return '';
    const withoutScripts = value.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ');
    const withoutStyles = withoutScripts.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ');
    const withoutTags = withoutStyles.replace(/<[^>]+>/g, ' ');
    return withoutTags
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim();
};

const sanitizeBlogHtml = (value: string): string => {
    const removeUnsafeMarkup = (html: string): string => html
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<(object|embed)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
        .replace(/<(?:object|embed|base|meta|link)\b[^>]*\/?\s*>/gi, '')
        .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
        .replace(/\s+(?:href|src|xlink:href|formaction|srcdoc)\s*=\s*(["'])\s*(?:javascript|vbscript|data\s*:\s*text\/html)[\s\S]*?\1/gi, '')
        .replace(/\s+style\s*=\s*(["'])[\s\S]*?(?:expression\s*\(|url\s*\(\s*(?:['"]?\s*)javascript:)[\s\S]*?\1/gi, '');

    if (typeof DOMParser === 'undefined') {
        return removeUnsafeMarkup(value);
    }

    const documentFragment = new DOMParser().parseFromString(value, 'text/html');
    documentFragment
        .querySelectorAll('script, object, embed, base, meta, link')
        .forEach(element => element.remove());

    documentFragment.body.querySelectorAll('*').forEach(element => {
        Array.from(element.attributes).forEach(attribute => {
            const name = attribute.name.toLowerCase();
            const normalizedValue = attribute.value.trim().toLowerCase();

            if (name.startsWith('on') || name === 'srcdoc') {
                element.removeAttribute(attribute.name);
                return;
            }

            if (
                ['href', 'src', 'xlink:href', 'formaction'].includes(name)
                && /^(?:javascript|vbscript|data\s*:\s*text\/html)/i.test(normalizedValue)
            ) {
                element.removeAttribute(attribute.name);
                return;
            }

            if (
                name === 'style'
                && /(?:expression\s*\(|url\s*\(\s*(?:['"]?\s*)javascript:)/i.test(attribute.value)
            ) {
                element.removeAttribute(attribute.name);
            }
        });
    });

    return documentFragment.body.innerHTML;
};

interface FaqQuestionSchema {
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
        '@type': 'Answer';
        text: string;
    };
}

const extractFaqSchema = (html?: string, articleUrl?: string): Record<string, unknown> | null => {
    if (!html) return null;

    const visibleArticleText = stripArticleText(html).toLowerCase();
    const questions = new Map<string, FaqQuestionSchema>();
    const scriptPattern = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
    let match: RegExpExecArray | null;

    while ((match = scriptPattern.exec(html)) !== null) {
        if (!/\btype\s*=\s*(["'])application\/ld\+json\1/i.test(match[1])) continue;

        try {
            const parsed = JSON.parse(match[2]);
            const nodes = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.['@graph'])
                    ? parsed['@graph']
                    : [parsed];

            nodes
                .filter((node: any) => node?.['@type'] === 'FAQPage' && Array.isArray(node.mainEntity))
                .flatMap((node: any) => node.mainEntity)
                .forEach((question: any) => {
                    const name = stripArticleText(String(question?.name || ''));
                    const answer = stripArticleText(String(question?.acceptedAnswer?.text || ''));

                    if (
                        !name
                        || !answer
                        || !visibleArticleText.includes(name.toLowerCase())
                        || !visibleArticleText.includes(answer.toLowerCase())
                    ) return;

                    questions.set(name.toLowerCase(), {
                        '@type': 'Question',
                        name,
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: answer
                        }
                    });
                });
        } catch {
            // Invalid embedded metadata is ignored; visible article HTML is still rendered safely.
        }
    }

    if (questions.size === 0) return null;

    return {
        '@type': 'FAQPage',
        ...(articleUrl ? { '@id': `${articleUrl}#faq` } : {}),
        mainEntity: Array.from(questions.values())
    };
};

const getWordCount = (value: string): number => (
    value.match(/[A-Za-z0-9][A-Za-z0-9'-]*/g)?.length || 0
);

const showImageFallback = (target: HTMLImageElement, title?: string): void => {
    target.style.display = 'none';
    const parent = target.parentElement;
    if (!parent || parent.querySelector('.img-fallback')) return;

    const fallback = document.createElement('div');
    fallback.className = 'img-fallback w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center';

    const initial = document.createElement('span');
    initial.style.color = '#ff5a0a';
    initial.style.fontSize = '2rem';
    initial.style.fontWeight = '900';
    initial.textContent = (title || 'T').trim().charAt(0).toUpperCase() || 'T';

    fallback.appendChild(initial);
    parent.appendChild(fallback);
};

const slugifyThumbnailSegment = (value?: string): string => String(value || '')
    .trim()
    .toLowerCase()
    .split('/')
    .filter(Boolean)
    .pop() || '';

const getGeneratedBlogThumbnail = (article?: any): string => {
    if (!article) return '';
    const thumbnailSlug = slugifyThumbnailSegment(article.parent_slug || article.parentSlug || article.slug);
    return thumbnailSlug ? `${GENERATED_THUMBNAIL_PREFIX}/${thumbnailSlug}.webp` : '';
};

const getBlogThumbnail = (article?: any): string => {
    if (!article) return '';
    const explicitImage = String(article.image || '').trim();
    if (explicitImage.startsWith(`${GENERATED_THUMBNAIL_PREFIX}/`)) return explicitImage;
    return getGeneratedBlogThumbnail(article) || explicitImage;
};

const getBlogHreflangs = (alternatives: any[] = []): { lang: string; url: string }[] => {
    const entries = alternatives.map((alt: any) => ({
        lang: alt.hreflang || getBlogLanguageTag(alt.lang, alt.parent_slug),
        url: getArticleUrl(alt, alt.lang),
    }));
    const english = entries.find(entry => entry.lang === 'en');

    return english
        ? [...entries, { lang: 'x-default', url: english.url }]
        : entries;
};

const handleBlogImageError = (target: HTMLImageElement, article?: any): void => {
    const attempted = new Set(String(target.dataset.attemptedFallbacks || '').split('|').filter(Boolean));
    attempted.add(target.getAttribute('src') || '');
    const fallbackSrc = [getGeneratedBlogThumbnail(article), String(article?.image || '').trim()]
        .find(candidate => candidate && !attempted.has(candidate));
    if (fallbackSrc) {
        target.dataset.attemptedFallbacks = [...attempted].join('|');
        target.src = fallbackSrc;
        return;
    }

    showImageFallback(target, article?.title);
};

const Blog: React.FC<BlogProps> = () => {
    const { slug, lang } = useParams<{ slug?: string; lang?: string }>();
    const [articles, setArticles] = useState<any[]>([]);
    const [activeArticleFromApi, setActiveArticleFromApi] = useState<any | null>(null);
    const [activeArticleContent, setActiveArticleContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeArticleId, setActiveArticleId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [view, setView] = useState<'list' | 'article'>(slug ? 'article' : 'list');

    // Fetch articles on mount
    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const currentLang = lang || 'en';
                const response = await fetch(`/api/blog/articles?lang=${currentLang}`);
                const data = await response.json();
                if (Array.isArray(data)) {
                    setArticles(data.map(sanitizeBlogArticle));
                } else {
                    console.error('Expected array from /api/blog/articles, got:', data);
                    setArticles([]);
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching articles:', error);
                setLoading(false);
            }
        };
        fetchArticles();
    }, [lang]);

    useEffect(() => {
        if (slug) {
            const fetchFullContent = async () => {
                try {
                    const currentLang = lang || 'en';
                    const response = await fetch(`/api/blog/articles/${slug}?lang=${currentLang}`);
                    if (response.ok) {
                        const data = await response.json();
                        const sanitizedArticle = sanitizeBlogArticle(data);
                        setActiveArticleFromApi(sanitizedArticle);
                        setActiveArticleContent(sanitizedArticle.content);
                        setActiveArticleId(sanitizedArticle.id);
                        setView('article');
                    }
                } catch (error) {
                    console.error('Error fetching article content:', error);
                }
            };
            fetchFullContent();
        } else {
            setActiveArticleFromApi(null);
            setActiveArticleContent(null);
        }
    }, [slug, lang]);


    // Data loaded from API


    const categories = ['All', 'Reviews', 'SEO', 'Marketing', 'Guides', 'Crypto', 'Strategy', 'Analytics'];

    useEffect(() => {
        if (loading) return;
        if (slug) {
            const currentLang = lang || 'en';
            // Match slug directly, or with lang prefix (seed data uses "it/some-slug" format)
            const article = articles.find(a => 
                (a.lang === currentLang && (a.slug === slug || a.slug.endsWith(`/${slug}`))) ||
                (a.slug === slug || a.slug === `${currentLang}/${slug}` || a.slug.endsWith(`/${slug}`))
            );
            if (article) {
                setActiveArticleId(article.id);
                setView('article');
            } else if (activeArticleFromApi) {
                // Article content loaded directly from API even if not in list
                setActiveArticleId(activeArticleFromApi.id);
                setView('article');
            } else if (!activeArticleContent && articles.length > 0) {
                // Only redirect if we have the article list loaded and still no match
                // Don't redirect immediately - the direct API fetch might still be in progress
                setView('article');
            }
        } else {
            setView('list');
            setActiveArticleId(null);
        }
    }, [slug, articles, loading, activeArticleContent, activeArticleFromApi]);



    // SEO Injection Helper


    const handleRSS = () => {
        const rssItems = articles.map(item => `
        <item>
            <title>${item.title}</title>
            <link>${getArticleUrl(item, lang)}</link>
            <description>${item.excerpt}</description>
            <pubDate>${new Date(item.date).toString() !== 'Invalid Date' ? new Date(item.date).toUTCString() : new Date().toUTCString()}</pubDate>
            <guid>${getArticleUrl(item, lang)}</guid>
        </item>
      `).join('');

        const rssContent = `<?xml version="1.0" encoding="UTF-8" ?>
        <rss version="2.0">
        <channel>
            <title>Traffic Creator Journal</title>
            <link>https://traffic-creator.com</link>
            <description>Digital Growth Insights</description>
            ${rssItems}
        </channel>
        </rss>`;

        const blob = new Blob([rssContent], { type: "text/xml" });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    const activeArticle = articles.find(a => a.id === activeArticleId) || activeArticleFromApi;
    const activeArticleImage = getBlogThumbnail(activeArticle);
    const relatedArticles = activeArticle ? articles.filter(a => a.category === activeArticle.category && a.id !== activeArticle.id) : [];
    const activeArticleUrl = activeArticle ? getArticleUrl(activeArticle, lang) : '';
    const activeArticleFaqSchema = extractFaqSchema(activeArticleContent || undefined, activeArticleUrl);
    const sanitizedActiveArticleContent = activeArticleContent
        ? sanitizeBlogHtml(softenBlogClaimText(activeArticleContent))
        : '';
    const activeArticlePlainText = activeArticleContent
        ? stripArticleText(activeArticleContent)
        : String(activeArticle?.excerpt || '');
    const activeArticleWordCount = getWordCount(activeArticlePlainText);
    const activeAuthorName = activeArticle?.author || 'Traffic Creator Team';
    const activeArticleLang = activeArticle?.lang || lang || 'en';
    const activeArticleFamily = activeArticle?.parent_slug || activeArticle?.slug;
    const handleArticleContentClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        const target = event.target;
        const anchor = target instanceof Element ? target.closest('a[href]') : null;
        if (!(anchor instanceof HTMLAnchorElement)) return;

        trackAffiliateLinkClick(anchor, event.currentTarget, {
            page: activeArticleUrl,
            cluster: activeArticleFamily || activeArticle?.category || 'blog',
            language: activeArticleLang,
        });
    };
    const activeArticleHreflang = getBlogLanguageTag(activeArticleLang, activeArticleFamily);
    const activeAuthorId = `${SITE_URL}/about#${activeAuthorName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'author'}`;
    const blogPath = `${lang && lang !== 'en' ? `/${lang}` : ''}/blog`;
    const blogUrl = `https://traffic-creator.com${blogPath}`;
    const blogCollectionSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${blogUrl}#collection`,
        "name": "Traffic Creator Blog",
        "url": blogUrl,
        "description": "Expert advice on website traffic, SEO strategy, analytics, and digital marketing from the Traffic Creator team.",
        "isPartOf": {
            "@type": "WebSite",
            "@id": "https://traffic-creator.com/#website",
            "name": "Traffic Creator",
            "url": "https://traffic-creator.com/"
        },
        "publisher": {
            "@type": "Organization",
            "@id": "https://traffic-creator.com/#organization",
            "name": "Traffic Creator",
            "url": "https://traffic-creator.com/",
            "logo": {
                "@type": "ImageObject",
                "url": "https://traffic-creator.com/favicon.svg"
            }
        },
        "mainEntity": {
            "@type": "Blog",
            "@id": `${blogUrl}#blog`,
            "name": "Traffic Creator Blog",
            "blogPost": articles.slice(0, 12).map(article => ({
                "@type": "BlogPosting",
                "headline": article.title,
                "description": article.seoDescription || article.excerpt,
                "author": {
                    "@type": "Person",
                    "name": article.author || "Traffic Creator Team"
                },
                "datePublished": article.date && new Date(article.date).toString() !== 'Invalid Date'
                    ? new Date(article.date).toISOString()
                    : undefined,
                "image": toAbsoluteImageUrl(getBlogThumbnail(article)),
                "url": getArticleUrl(article, lang)
            })).filter(article => article.headline)
        },
        "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://traffic-creator.com/" },
                { "@type": "ListItem", "position": 2, "name": "Blog", "item": blogUrl }
            ]
        }
    };

    const seoProps = (view === 'article' && activeArticle) ? {
        title: activeArticle.title,
        description: activeArticle.seoDescription,
        keywords: (activeArticle.tags || []).join(', '),
        type: 'article' as const,
        image: activeArticleImage,
        hreflangs: getBlogHreflangs(activeArticle.alternatives || []),
        schema: {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "BlogPosting",
                    "@id": `${activeArticleUrl}#article`,
                    "headline": activeArticle.title,
                    "description": activeArticle.seoDescription || activeArticle.excerpt,
                    "datePublished": getSafeIsoDate(activeArticle.date),
                    "dateModified": getSafeIsoDate(activeArticle.updatedAt || activeArticle.date),
                    "author": { "@id": activeAuthorId },
                    "publisher": { "@id": `${SITE_URL}/#organization` },
                    "image": { "@id": `${activeArticleUrl}#primaryimage` },
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": activeArticleUrl
                    },
                    "url": activeArticleUrl,
                    "wordCount": activeArticleWordCount,
                    "articleBody": activeArticlePlainText.slice(0, 500),
                    "articleSection": activeArticle.category || 'Blog',
                    "keywords": (activeArticle.tags || []).join(', '),
                    "inLanguage": activeArticleHreflang,
                    "isPartOf": {
                        "@type": "Blog",
                        "inLanguage": activeArticleHreflang
                    },
                    ...(activeArticle.parent_slug && activeArticleLang !== 'en' ? {
                        "translationOfWork": {
                            "@type": "BlogPosting",
                            "inLanguage": "en",
                            "url": `${SITE_URL}/blog/${activeArticle.parent_slug}`
                        }
                    } : {}),
                    ...(activeArticleLang === 'en' && activeArticle.alternatives?.length ? {
                        "workTranslation": activeArticle.alternatives
                            .filter((alternative: any) => alternative.lang !== 'en')
                            .map((alternative: any) => ({
                                "@type": "BlogPosting",
                                "inLanguage": getBlogLanguageTag(
                                    alternative.lang,
                                    activeArticleFamily,
                                ),
                                "url": getArticleUrl(alternative, alternative.lang),
                            }))
                    } : {})
                },
                {
                    "@type": "Person",
                    "@id": activeAuthorId,
                    "name": activeAuthorName,
                    "jobTitle": activeArticle.role || 'SEO & Traffic Expert',
                    "url": `${SITE_URL}/about`,
                    "worksFor": { "@id": `${SITE_URL}/#organization` }
                },
                {
                    "@type": "Organization",
                    "@id": `${SITE_URL}/#organization`,
                    "name": "Traffic Creator",
                    "url": `${SITE_URL}/`,
                    "logo": {
                        "@type": "ImageObject",
                        "url": `${SITE_URL}/favicon.svg`
                    }
                },
                {
                    "@type": "ImageObject",
                    "@id": `${activeArticleUrl}#primaryimage`,
                    "url": toAbsoluteImageUrl(activeArticleImage),
                    "caption": activeArticle.title
                },
                {
                    "@type": "BreadcrumbList",
                    "@id": `${activeArticleUrl}#breadcrumb`,
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                        { "@type": "ListItem", "position": 2, "name": "Blog", "item": blogUrl },
                        { "@type": "ListItem", "position": 3, "name": activeArticle.title, "item": activeArticleUrl }
                    ]
                },
                ...(activeArticleFaqSchema ? [activeArticleFaqSchema] : [])
            ]
        }
    } : {
        title: "SEO & Traffic Generation Blog 2026 | Traffic Creator Insights",
        description: "Expert guides for 2026 on website traffic quality, CTR analysis, technical SEO, and analytics-backed growth planning.",
        keywords: "seo blog 2026, traffic generation tips, search visibility strategy, digital marketing insights",
        type: 'website' as const,
        schema: blogCollectionSchema
    };
    useEffect(() => {
        document.title = seoProps.title;
    }, [seoProps.title]);

    // JSON-LD Schema Generator


    // Filter Logic
    const filteredArticles = articles.filter(article => {
        const title = String(article.title || '');
        const excerpt = String(article.excerpt || '');
        const search = String(searchTerm || '').toLowerCase();
        const matchesSearch = title.toLowerCase().includes(search) ||
            excerpt.toLowerCase().includes(search);
        const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    if (view === 'article' && activeArticle) {
        return (
            <div className="min-h-screen bg-white font-sans text-gray-900 pb-20 selection:bg-[#ff5a0a]/30 underline-offset-4">
                <SEO {...seoProps} />
                {/* Inject custom blog icon CSS for branded SVG icons */}
                <style dangerouslySetInnerHTML={{ __html: BLOG_ICON_CSS }} />

                <ScrollProgress />

                {/* Article Header - Keeping Dark for impact, but could be changed if requested */}
                <div className="relative h-[60vh] bg-gray-900 group">
                    <img
                        src={activeArticleImage}
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-55 transition-opacity duration-700"
                        alt={activeArticle.title}
                        width={1200}
                        height={630}
                        fetchPriority="high"
                        decoding="async"
                        onError={(e) => {
                            handleBlogImageError(e.currentTarget, activeArticle);
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/20 to-transparent" aria-hidden="true"></div>

                    <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center z-10">
                        <a href={lang ? `/${lang}/blog` : '/blog'} className="text-white flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:text-[#ff5a0a] transition-colors bg-black/20 backdrop-blur-md px-4 py-2 rounded-full">
                            <ArrowLeft size={14} /> Back
                        </a>
                    </div>

                    <div className="absolute bottom-0 left-0 w-full p-8 md:p-16">
                        <div className="max-w-4xl mx-auto animate-in slide-in-from-bottom-10 fade-in duration-700">
                            <div className="flex gap-2 mb-6">
                                <span className="bg-[#ff5a0a] text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest">
                                    {activeArticle.category || 'Article'}
                                </span>
                            </div>
                            <h1 className="text-4xl md:text-6xl font-black text-white leading-[1.1] mb-8 drop-shadow-lg">
                                {activeArticle.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-8 text-gray-200 text-sm font-medium border-t border-white/20 pt-8">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-white font-bold">
                                        {(activeArticle.author || 'T').charAt(0)}
                                    </div>
                                    <div>
                                        <div className="text-white font-bold">{activeArticle.author}</div>
                                        <div className="text-xs text-gray-300 uppercase tracking-wide">{activeArticle.role}</div>
                                    </div>
                                </div>
                                <span className="flex items-center gap-2"><Calendar size={16} /> {activeArticle.date}</span>
                                <span className="flex items-center gap-2"><Clock size={16} /> {activeArticle.readTime}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Two-Column Layout for Article + Sidebar */}
                <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-4 gap-12">

                    {/* Main Content */}
                    <main className="lg:col-span-3">
                        <article>
                            <p className="text-xl text-gray-600 mb-10 font-medium border-l-4 border-[#ff5a0a] pl-6 italic">
                                {activeArticle.excerpt}
                            </p>

                            {/* Dynamic Article Content - Loaded from Database */}
                            {activeArticleContent ? (
                                <div
                                    className="article-content-wrapper text-gray-800"
                                    onClick={handleArticleContentClick}
                                    dangerouslySetInnerHTML={{
                                        __html: replaceEmojisWithIcons(sanitizedActiveArticleContent)
                                    }}
                                />
                            ) : (
                                <div className="prose prose-lg prose-slate max-w-none leading-relaxed prose-headings:font-black prose-headings:tracking-tight prose-a:text-[#ff5a0a] prose-img:rounded-sm space-y-6">
                                    <div className="animate-pulse space-y-4">
                                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                        <div className="h-4 bg-gray-200 rounded"></div>
                                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                    <p className="text-gray-400">
                                        Loading article content...
                                    </p>
                                </div>
                            )}

                            {/* Tags Footer */}
                            <div className="mt-20 pt-10 border-t border-gray-200">
                                <div className="flex flex-wrap gap-2 mb-8">
                                    {(activeArticle.tags || []).map(tag => (
                                        <span key={tag} className="bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600 px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm cursor-pointer">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Author Bio Card – E-E-A-T Signal */}
                            <div className="mt-10 bg-gray-50 border border-gray-200 rounded-sm p-8 flex flex-col sm:flex-row gap-6 items-start">
                                <div className="w-16 h-16 rounded-sm bg-gradient-to-br from-[#ff5a0a] to-orange-400 flex items-center justify-center text-white text-2xl font-black flex-shrink-0">
                                    {activeArticle.author?.charAt(0) || 'T'}
                                </div>
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-[#ff5a0a] mb-1">Written by</p>
                                    <h3 className="text-xl font-black text-gray-900 mb-0.5">{activeArticle.author || 'Traffic Creator Team'}</h3>
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">{activeArticle.role || 'SEO & Traffic Expert · Traffic Creator'}</p>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        The Traffic Creator editorial team is made up of digital marketing practitioners with 8+ years of experience in SEO, paid traffic, and web analytics. All content is reviewed for accuracy and updated regularly.
                                    </p>
                                    <a href="/about" className="inline-flex items-center gap-1 mt-4 text-xs font-bold text-[#ff5a0a] uppercase tracking-widest hover:underline">
                                        About Traffic Creator →
                                    </a>
                                </div>
                            </div>
                        </article>
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-1 space-y-12">
                        {/* Share */}
                        <div>
                            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-4">Share This</h4>
                            <div className="flex gap-2">
                                <button className="p-3 rounded-full border border-gray-200 text-gray-500 hover:text-[#ff5a0a] hover:border-[#ff5a0a] transition-all" onClick={() => { navigator.clipboard.writeText(window.location.href); alert('Link copied!'); }}>
                                    <Share2 size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Related Articles */}
                        {relatedArticles.length > 0 && (
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-6">Related Reads</h4>
                                <div className="space-y-6">
                                    {relatedArticles.slice(0, 5).map(article => (
                                        <a key={article.id} href={getArticlePath(article, lang)} className="group block">
                                            <div className="aspect-[1200/630] bg-gray-100 mb-2 overflow-hidden rounded-sm">
                                                <img
                                                    src={getBlogThumbnail(article)}
                                                    alt={article.title}
                                                    className="w-full h-full object-contain transition-opacity duration-300 opacity-95 group-hover:opacity-100"
                                                    onError={(e) => {
                                                        handleBlogImageError(e.currentTarget, article);
                                                    }}
                                                />
                                            </div>
                                            <h5 className="text-sm font-bold text-gray-900 group-hover:text-[#ff5a0a] leading-tight mb-1">{article.title}</h5>
                                            <span className="text-[10px] text-gray-400 font-bold uppercase">{article.date}</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Newsletter Box */}
                        <div className="bg-gray-50 border border-gray-100 text-gray-900 p-6 text-center rounded-sm">
                            <Mail className="mx-auto mb-4 text-[#ff5a0a]" size={24} />
                            <h3 className="text-lg font-black uppercase tracking-tight mb-2">Join the Inner Circle</h3>
                            <p className="text-gray-500 text-xs mb-6">Get exclusive SEO tips delivered to your inbox.</p>
                            <input type="email" placeholder="Email" className="bg-white border border-gray-200 text-gray-900 placeholder-gray-400 px-4 py-2 w-full outline-none focus:ring-1 focus:ring-[#ff5a0a] mb-2 text-sm" />
                            <button className="w-full bg-[#ff5a0a] text-white px-4 py-2 font-bold uppercase text-xs tracking-wider hover:bg-black hover:text-white transition-colors">
                                Subscribe
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        )
    }

    // --- LIST VIEW ---
    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">
            <SEO {...seoProps} />
            <div className="border-b border-gray-200 sticky top-0 bg-white/95 backdrop-blur-md z-50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-2 text-gray-500 hover:text-[#ff5a0a] transition-colors text-xs font-bold uppercase tracking-wider">
                        <ArrowLeft size={16} /> Back to Home
                    </a>
                    <div className="flex items-center gap-2">
                        <span className="text-xl font-black text-[#ff5a0a] tracking-tight">TRAFFIC</span>
                        <span className="text-[10px] font-bold bg-black text-white px-1.5 py-0.5 rounded-sm uppercase tracking-wide">Journal</span>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-20">
                {/* Hero Section */}
                <div className="text-center max-w-3xl mx-auto mb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6 leading-[0.9] text-gray-900">
                        Digital <span className="text-[#ff5a0a]">Growth</span><br />Insights.
                    </h1>
                    <p className="text-xl text-gray-500 leading-relaxed mb-10">
                        Expert advice on digital marketing, SEO strategies, and traffic optimization for the modern web.
                    </p>

                    {/* Search Bar */}
                    <div className="relative max-w-xl mx-auto shadow-xl">
                        <input
                            type="text"
                            placeholder="Search articles..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white border border-gray-200 py-4 pl-12 pr-4 text-sm font-bold text-gray-900 outline-none focus:border-[#ff5a0a] transition-colors rounded-sm"
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap justify-center gap-4 mb-16">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-6 py-2 text-xs font-bold uppercase tracking-widest border transition-all rounded-full
                            ${selectedCategory === cat
                                    ? 'bg-[#ff5a0a] text-white border-[#ff5a0a] shadow-lg'
                                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400 hover:text-black'
                                }
                        `}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* Articles Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
                    {filteredArticles.length === 0 ? (
                        <div className="col-span-3 text-center py-20 text-gray-400">
                            <Filter size={48} className="mx-auto mb-4 opacity-20" />
                            <p>No articles found matching your criteria.</p>
                        </div>
                    ) : (
                        filteredArticles.map((article, index) => (
                            <article key={article.id} className="h-full animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: `${index * 100}ms` }}>
                                <a href={getArticlePath(article, lang)} className="group flex h-full flex-col">
                                <div className="aspect-[1200/630] overflow-hidden bg-gray-100 mb-6 relative rounded-sm group shadow-sm">
                                    <img
                                        src={getBlogThumbnail(article)}
                                        alt={article.title}
                                        className="w-full h-full object-contain transition-opacity duration-500 opacity-95 group-hover:opacity-100"
                                        loading="lazy"
                                        onError={(e) => {
                                            handleBlogImageError(e.currentTarget, article);
                                        }}
                                    />
                                </div>
                                <div className="flex-1 flex flex-col">
                                    <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex flex-wrap items-center gap-2">
                                        {article.category && (
                                            <>
                                                <span className="bg-gray-100 border border-gray-200 px-2 py-1 text-[10px] font-black text-[#ff5a0a]">
                                                    {article.category}
                                                </span>
                                                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                                            </>
                                        )}
                                        {article.date || ''} <span className="w-1.5 h-1.5 bg-[#ff5a0a] rounded-full"></span> {article.readTime || ''}
                                    </div>
                                    <h2 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-[#ff5a0a] transition-colors leading-tight tracking-tight">
                                        {article.title}
                                    </h2>
                                    <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1 line-clamp-3">
                                        {article.excerpt}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#ff5a0a] group-hover:translate-x-2 transition-transform duration-300 mt-auto">
                                        Read Article <ArrowRight size={14} />
                                    </div>
                                </div>
                                </a>
                            </article>
                        ))
                    )}
                </div>
            </div>

            <footer className="bg-gray-900 text-white py-12 px-6 mt-20">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="text-xl font-black tracking-tight">TRAFFIC CREATOR</div>
                    <div className="flex gap-6 text-xs font-bold text-gray-500 uppercase tracking-widest">
                        <button onClick={handleRSS} className="hover:text-white transition-colors flex items-center gap-2">
                            <Rss size={14} /> RSS Feed
                        </button>
                        <button className="hover:text-white transition-colors">Newsletter</button>
                        <a href="/about" className="hover:text-white transition-colors">About Us</a>
                        <a href="/legal/impressum" className="hover:text-white transition-colors">Impressum</a>
                    </div>
                    <div className="text-xs text-gray-600">© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt)</div>
                </div>
            </footer>
        </div>
    );
};

// Isolated component to prevent re-renders of the main page
const ScrollProgress = () => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const totalScroll = document.documentElement.scrollTop;
            const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            if (windowHeight === 0) return;
            const scroll = totalScroll / windowHeight;
            setProgress(scroll);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div
            className="fixed top-0 left-0 h-1 bg-[#ff5a0a] z-[60] transition-all duration-100 ease-out"
            style={{ width: `${progress * 100}%` }}
        />
    );
};

export default Blog;
